import React from 'react';
import { Microscope, Target, Users, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import Seo from '../components/Seo';

const About = () => {
  const values = [
    {
      icon: Microscope,
      title: 'Scientific Excellence',
      description: 'We maintain the highest standards of purity and potency through rigorous third-party testing and quality control protocols.'
    },
    {
      icon: Target,
      title: 'Precision Manufacturing',
      description: 'State-of-the-art facilities and pharmaceutical-grade processes ensure consistent, reliable results with every product.'
    },
    {
      icon: Users,
      title: 'Customer First',
      description: 'Your success is our priority. We provide expert guidance, responsive support, and educational resources.'
    },
    {
      icon: Award,
      title: 'Industry Leadership',
      description: 'Trusted by researchers and wellness professionals worldwide for our commitment to quality and transparency.'
    }
  ];

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: '#0C0A09' }}>
      <Seo
        path="/about"
        title="About Eman's Peppers | Trusted Peptide Research Source"
        description="Learn about Eman's Peppers — our commitment to research-grade quality, transparent sourcing, and personal customer service for the peptide research community."
      />
      {/* Hero Section */}
      <section className="relative py-24" style={{ backgroundColor: '#1A1513' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="section-title">About CM Peptides</h1>
            <p className="section-subtitle">
              Pioneering the future of peptide research with uncompromising quality standards and scientific integrity.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20" style={{ backgroundColor: '#0C0A09' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-medium mb-6" style={{ color: '#F7F3EE', fontFamily: 'Playfair Display, serif' }}>
                Our Story
              </h2>
              <div className="space-y-4 text-lg leading-relaxed" style={{ color: '#B5A79E', fontFamily: 'Manrope, sans-serif' }}>
                <p>
                  CM Peptides was founded with a singular mission: to provide researchers and wellness professionals 
                  with access to high-quality peptides for their research needs.
                </p>
                <p>
                  What started as a small operation has grown into a trusted source for research-grade 
                  peptides, serving customers worldwide. Our commitment to quality and customer satisfaction remains unchanged.
                </p>
                <p>
                  We maintain strict quality control throughout our supply chain and work with specialized manufacturing 
                  facilities to ensure consistent, reliable research materials for the scientific community.
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1614935151651-0bea6508db6b"
                alt="Laboratory Research"
                className="rounded-lg shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-teal-100 text-teal-600 rounded-lg">
                        <value.icon className="h-6 w-6" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Assurance */}
      <section className="py-24" style={{ backgroundColor: '#1A1513' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1634872583967-6417a8638a59"
                alt="Quality Control"
                className="rounded-lg w-full h-auto opacity-80"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-medium mb-6" style={{ color: '#F7F3EE', fontFamily: 'Playfair Display, serif' }}>
                Quality Standards
              </h2>
              <div className="space-y-4 text-lg leading-relaxed" style={{ color: '#B5A79E', fontFamily: 'Manrope, sans-serif' }}>
                <p>
                  We maintain stringent quality control measures throughout our entire supply chain to ensure you receive 
                  reliable, consistent research materials every time.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <span className="font-bold mr-2" style={{ color: '#D4A373' }}>✓</span>
                    <span>Manufactured in specialized facilities with strict protocols</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-bold mr-2" style={{ color: '#D4A373' }}>✓</span>
                    <span>Proper storage and handling throughout distribution</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-bold mr-2" style={{ color: '#D4A373' }}>✓</span>
                    <span>Quality control at every stage of production</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-bold mr-2" style={{ color: '#D4A373' }}>✓</span>
                    <span>Commitment to providing reliable research materials</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-24 bg-gradient-to-r" style={{ backgroundImage: 'linear-gradient(to right, #0C0A09, #1A1513)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-medium mb-6" style={{ color: '#F7F3EE', fontFamily: 'Playfair Display, serif' }}>
              Our Mission
            </h2>
            <p className="text-xl leading-relaxed mb-8" style={{ color: '#D4A373', fontFamily: 'Manrope, sans-serif' }}>
              To advance human health and performance through the provision of the highest quality research peptides, 
              backed by scientific rigor, ethical practices, and unwavering commitment to our customers' success.
            </p>
            <p className="text-lg" style={{ color: '#B5A79E', fontFamily: 'Manrope, sans-serif' }}>
              We envision a world where cutting-edge peptide science is accessible to all who seek to optimize their 
              health, performance, and longevity through evidence-based approaches.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
